import { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowClockwise, Plus, PencilSimple, Trash, Link as LinkIcon, Copy, Check, X, Globe } from "@phosphor-icons/react";

const EMPTY_LINK = { url: "", label: "" };
const EMPTY_FORM = {
  id: null,
  name: "",
  links: [{ ...EMPTY_LINK }],
  active: true,
};

export default function MyRotors() {
  const [rotors, setRotors] = useState([]);
  const [limit, setLimit] = useState(10);
  const [maxLinks, setMaxLinks] = useState(10);
  const [membership, setMembership] = useState("Free");
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [copiedSlug, setCopiedSlug] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/rotors");
      setRotors(data.rotors || []);
      setLimit(data.limit || 10);
      setMaxLinks(data.max_links_per_rotor || 10);
      setMembership(data.membership || "Free");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    if (rotors.length >= limit) {
      toast.error(`You've hit the ${limit} rotor limit on the ${membership} plan.`);
      return;
    }
    setForm(EMPTY_FORM);
    setEditingId(null);
    setShowForm(true);
  };

  const openEdit = (r) => {
    setForm({
      id: r.id,
      name: r.name,
      links: r.links?.length ? r.links.map((l) => ({ ...l })) : [{ ...EMPTY_LINK }],
      active: !!r.active,
    });
    setEditingId(r.id);
    setShowForm(true);
  };

  const addLink = () => {
    if (form.links.length >= maxLinks) {
      toast.error(`A rotor can hold at most ${maxLinks} links.`);
      return;
    }
    setForm({ ...form, links: [...form.links, { ...EMPTY_LINK }] });
  };

  const removeLink = (idx) => {
    const next = form.links.filter((_, i) => i !== idx);
    setForm({ ...form, links: next.length ? next : [{ ...EMPTY_LINK }] });
  };

  const updateLink = (idx, patch) => {
    const next = form.links.map((l, i) => (i === idx ? { ...l, ...patch } : l));
    setForm({ ...form, links: next });
  };

  const save = async () => {
    if (saving) return;
    const clean = form.links.filter((l) => (l.url || "").trim());
    if (!form.name.trim()) return toast.error("Please give this rotor a name");
    if (!clean.length) return toast.error("Add at least one link");
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        links: clean,
        active: !!form.active,
      };
      if (editingId) {
        await api.put(`/rotors/${editingId}`, payload);
        toast.success("Rotor saved");
      } else {
        await api.post("/rotors", payload);
        toast.success("Rotor created");
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY_FORM);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete rotor "${r.name}"? The public URL /r/${r.slug} will stop working.`)) return;
    try {
      await api.delete(`/rotors/${r.id}`);
      toast.success("Rotor deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const copyUrl = async (slug) => {
    const base = typeof window !== "undefined" ? window.location.origin : "";
    try {
      await navigator.clipboard.writeText(`${base}/r/${slug}`);
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(""), 1600);
    } catch { toast.error("Could not copy to clipboard"); }
  };

  return (
    <DashboardLayout>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">Promotion</div>
          <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white inline-flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
            <ArrowClockwise size={28} weight="fill" className="text-indigo-500" />
            TE Website Rotors
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 max-w-2xl">
            Group up to {maxLinks} website links per rotor and share one short URL. Each visit shows a different random site from the rotor.
          </p>
        </div>
        <button
          data-testid="rotor-create-btn"
          onClick={openCreate}
          disabled={rotors.length >= limit}
          className="inline-flex items-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white font-black px-5 py-2.5 text-sm shadow-sm disabled:opacity-50 transition-colors duration-200"
        >
          <Plus size={16} weight="bold" /> New rotor
        </button>
      </div>

      <div className="text-xs text-slate-500 mb-5" data-testid="rotor-usage">
        Using <span className="font-black text-slate-700 dark:text-slate-300">{rotors.length}</span> of <span className="font-black text-slate-700 dark:text-slate-300">{limit}</span> rotors on the <span className="font-black text-indigo-600 dark:text-indigo-400">{membership}</span> plan.
      </div>

      {showForm && (
        <div data-testid="rotor-form" className="rounded-2xl border border-indigo-300 dark:border-indigo-500/40 bg-indigo-50 dark:bg-indigo-500/5 p-5 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="text-base font-black text-slate-900 dark:text-white">{editingId ? "Edit rotor" : "New rotor"}</div>
            <button data-testid="rotor-form-close" onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }} className="text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"><X size={20} /></button>
          </div>

          <div className="mb-4">
            <label className="block">
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-500">Rotor name</span>
              <input
                data-testid="rotor-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="My Business Links"
                className="w-full mt-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
              />
            </label>
          </div>

          <div className="mb-3 flex items-center justify-between">
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">Links ({form.links.filter((l) => l.url.trim()).length} / {maxLinks})</div>
            <button
              data-testid="rotor-add-link"
              onClick={addLink}
              disabled={form.links.length >= maxLinks}
              className="text-xs font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-500 disabled:opacity-40"
            >
              + Add link
            </button>
          </div>
          <div className="space-y-2">
            {form.links.map((l, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className="h-8 w-8 rounded-full bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300 inline-flex items-center justify-center text-xs font-black shrink-0">{idx + 1}</div>
                <input
                  data-testid={`rotor-link-url-${idx}`}
                  value={l.url}
                  onChange={(e) => updateLink(idx, { url: e.target.value })}
                  placeholder="https://example.com"
                  className="flex-1 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                />
                <input
                  data-testid={`rotor-link-label-${idx}`}
                  value={l.label || ""}
                  onChange={(e) => updateLink(idx, { label: e.target.value })}
                  placeholder="label (optional)"
                  className="w-40 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm text-slate-900 dark:text-slate-100"
                />
                <button
                  data-testid={`rotor-link-remove-${idx}`}
                  onClick={() => removeLink(idx)}
                  className="h-8 w-8 rounded-full bg-slate-100 hover:bg-red-100 text-slate-500 hover:text-red-700 dark:bg-slate-800 dark:hover:bg-red-500/20 dark:hover:text-red-300 inline-flex items-center justify-center transition-colors"
                >
                  <X size={14} weight="bold" />
                </button>
              </div>
            ))}
          </div>

          <label data-testid="rotor-form-active" className="mt-4 flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 cursor-pointer w-fit">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="h-4 w-4 accent-indigo-500" />
            <div>
              <div className="text-sm font-bold text-slate-900 dark:text-white">Active</div>
              <div className="text-[11px] text-slate-500">Inactive rotors return 404 at their public URL</div>
            </div>
          </label>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <button
              data-testid="rotor-form-save"
              onClick={save}
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white font-black px-5 py-2.5 text-sm shadow-sm disabled:opacity-60 transition-colors duration-200"
            >
              <Check size={16} weight="bold" /> {editingId ? "Save changes" : "Create rotor"}
            </button>
            <button
              data-testid="rotor-form-cancel"
              onClick={() => { setShowForm(false); setEditingId(null); setForm(EMPTY_FORM); }}
              className="rounded-full px-4 py-2 text-sm font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-10 text-center text-slate-500">Loading…</div>
      ) : rotors.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-indigo-300 dark:border-indigo-500/40 bg-white dark:bg-slate-900 p-10 text-center">
          <ArrowClockwise size={32} weight="duotone" className="mx-auto text-indigo-500 mb-2" />
          <div className="font-black text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>No rotors yet</div>
          <div className="text-sm text-slate-500 mt-1">Create your first rotor to get a short URL that cycles through all your sites.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {rotors.map((r) => {
            const publicUrl = `${typeof window !== "undefined" ? window.location.origin : ""}/r/${r.slug}`;
            return (
              <div key={r.id} data-testid={`rotor-card-${r.slug}`} className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-black text-slate-900 dark:text-white truncate" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>{r.name}</div>
                    <div className="text-xs text-slate-500 mt-1">{r.links?.length || 0} link{(r.links?.length || 0) !== 1 ? "s" : ""} · random pick on load · {r.views || 0} views</div>
                  </div>
                  {!r.active && <div className="text-[10px] font-black uppercase tracking-widest text-amber-700 bg-amber-100 dark:bg-amber-500/20 dark:text-amber-300 rounded-full px-2 py-0.5 shrink-0">Paused</div>}
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <code className="flex-1 text-xs font-mono text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-lg px-3 py-2 truncate" data-testid={`rotor-url-${r.slug}`}>{publicUrl}</code>
                  <button
                    data-testid={`rotor-copy-${r.slug}`}
                    onClick={() => copyUrl(r.slug)}
                    title="Copy public rotor URL"
                    className="h-9 w-9 rounded-xl bg-slate-100 hover:bg-indigo-100 text-slate-600 hover:text-indigo-700 dark:bg-slate-800 dark:hover:bg-indigo-500/20 dark:hover:text-indigo-300 inline-flex items-center justify-center transition-colors"
                  >
                    {copiedSlug === r.slug ? <Check size={14} weight="bold" /> : <Copy size={14} weight="bold" />}
                  </button>
                </div>

                <div className="mt-3 space-y-1">
                  {(r.links || []).slice(0, 4).map((l, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-300 truncate">
                      <Globe size={12} weight="bold" className="text-indigo-500 shrink-0" />
                      <span className="truncate">{l.label || l.url}</span>
                    </div>
                  ))}
                  {(r.links || []).length > 4 && <div className="text-xs text-slate-400">+ {(r.links || []).length - 4} more</div>}
                </div>

                <div className="mt-4 flex items-center gap-2">
                  <a
                    data-testid={`rotor-open-${r.slug}`}
                    href={`/r/${r.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white font-black px-3 py-1.5 text-xs"
                  >
                    <LinkIcon size={12} weight="bold" /> Open rotor
                  </a>
                  <button
                    data-testid={`rotor-edit-${r.slug}`}
                    onClick={() => openEdit(r)}
                    className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 text-xs font-bold hover:underline"
                  >
                    <PencilSimple size={12} weight="bold" /> edit
                  </button>
                  <button
                    data-testid={`rotor-delete-${r.slug}`}
                    onClick={() => remove(r)}
                    className="inline-flex items-center gap-1 text-red-600 dark:text-red-400 text-xs font-bold hover:underline"
                  >
                    <Trash size={12} weight="bold" /> delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}
